import { jwtDecode } from 'jwt-decode';

import { setUserSessionContext } from '@/store/actions';
import { SESSION_COOKIE } from '@/utilities/constants/metadata';
import { getCookieValue } from '@/utilities/getters/cookieGetters';
import { getLocalAccountSession } from '@/utilities/getters/sessionContextGetters';

type AuthTokenPayload = {
  exp?: number;
  sub?: string | number;
};

export const getValidAuthTokenUserId = (authToken: string): string | null => {
  try {
    const { exp, sub } = jwtDecode<AuthTokenPayload>(authToken);
    if (
      sub === undefined ||
      sub === null ||
      typeof exp !== 'number' ||
      exp * 1000 <= Date.now()
    ) {
      return null;
    }

    return String(sub);
  } catch {
    return null;
  }
};

const getSessionStartTime = (): Date | null => {
  const value = getCookieValue(SESSION_COOKIE.START_TIME);
  if (!value) return null;

  const sessionStartTime = new Date(value);
  return Number.isNaN(sessionStartTime.valueOf()) ? null : sessionStartTime;
};

/**
 * True when Account UM-managed cookies represent a logged-in user.
 */
export const hasLobbySessionCookies = (): boolean => {
  const accountSession = getLocalAccountSession();
  if (!accountSession) {
    return false;
  }

  return getValidAuthTokenUserId(accountSession.authToken.jwt) !== null;
};

/**
 * Optimistically hydrates launch-critical session state from lobby cookies.
 * Account UM still initializes afterwards and reconciles the session.
 */
export const hydrateCookieSession = (): boolean => {
  const accountSession = getLocalAccountSession();
  if (!accountSession) {
    return false;
  }

  const authToken = accountSession.authToken.jwt;
  const authTokenUserId = getValidAuthTokenUserId(authToken);
  if (!authTokenUserId) {
    return false;
  }

  if (accountSession.userId && accountSession.userId !== authTokenUserId) {
    return false;
  }

  const sessionStartTime =
    getSessionStartTime() ||
    (accountSession.authToken.created
      ? new Date(accountSession.authToken.created)
      : null);
  setUserSessionContext({
    authToken: {
      jwt: authToken,
      created:
        accountSession.authToken.created ??
        new Date(sessionStartTime?.toISOString() ?? ''),
    },
    hasActiveSession: true,
    loginToken: accountSession.loginToken,
    ...(accountSession.sessionId
      ? { sessionId: accountSession.sessionId }
      : {}),
    sessionStartTime,
    region: accountSession.region,
    userId: authTokenUserId,
  });

  return true;
};
